import { createContext, useContext, useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { configureApi, fetchHealth, markSessionDead, resetAuthState } from '../api';
import StepUpModal from '../components/StepUpModal';
import IdleWarningModal from '../components/IdleWarningModal';
import SessionEndingNotice from '../components/SessionEndingNotice';

const AuthContext = createContext();

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 min (CJIS §5.4)
// Cross-tab activity clock. localStorage is shared by every tab of the
// session, so a background tab whose own clock ran out must not sign out a
// person who is actively working in another tab (P1.11 click-through
// 2026-09-16: two "spontaneous" sign-outs were exactly 30 min after the last
// click in a tab left open behind the active one). Writes are throttled.
const ACTIVITY_KEY = 'dc_last_activity';
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;
function readSharedActivity() {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
}
function writeSharedActivity(ts) {
  try { localStorage.setItem(ACTIVITY_KEY, String(ts)); } catch { /* storage unavailable */ }
}
const IDLE_CHECK_INTERVAL = 15_000;
// Real input that counts as "the person is here". Registered in the CAPTURE
// phase: `scroll` does not bubble, so a bubble-phase listener on document
// never sees scrolling inside a panel (the document viewer, the chat pane) —
// reading a long transcript with the wheel looked like 30 idle minutes.
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'click', 'wheel', 'touchstart', 'pointerdown'];
// The Portal dashboard that launched this tab keeps its OWN idle clock, on
// another origin, where localStorage can't be shared. The two exchange their
// last-activity time by postMessage so one session has one clock: working
// here keeps the dashboard tab alive, and "I'm still here" on either side
// answers both. The dashboard opens the exchange (it knows our origin from
// the launch URL); we only ever reply to an origin on this allowlist.
const IDLE_SYNC_MESSAGE = 'cs_idle_sync';
const PORTAL_PUBLIC_ORIGINS = ['https://chargestack.ai', 'https://www.chargestack.ai'];
// Tell the person this long before the absolute session ceiling ends the
// session — it is not an idle timeout, so no amount of activity avoids it.
const SESSION_END_NOTICE_LEAD_MS = 10 * 60 * 1000;
// Show the "are you still there?" warning this long before the idle signout,
// so the user can acknowledge presence instead of being kicked out cold.
const IDLE_WARNING_LEAD_MS = 2 * 60 * 1000;
// Absolute session ceiling — mirrors the backend SESSION_MAX_AGE_MINUTES
// (Tier 1). A saved token older than this would be rejected by the backend
// anyway; don't resume it, force a fresh Portal login (= fresh MFA).
const SESSION_MAX_AGE_MS = 8 * 60 * 60 * 1000;
// Refresh the Firebase ID token this many ms before its `exp` claim. Firebase
// rotates IDs every ~1h; 5 min lead time keeps every outbound call inside a
// fresh token without thundering against the Secure Token API.
const TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;
// Fallback cadence if we can't parse `exp` from the JWT for any reason.
const TOKEN_REFRESH_FALLBACK_MS = 45 * 60 * 1000;
// After a transient refresh failure, retry on this schedule before giving up.
const TOKEN_REFRESH_RETRY_MS = 30_000;

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN':
      return { ...state, user: action.user, token: action.token, loading: false, devMode: action.devMode ?? state.devMode };
    case 'LOGOUT':
      return { ...state, user: null, token: null, loading: false };
    case 'SET_DEV_MODE':
      return { ...state, devMode: action.devMode };
    case 'SET_PORTAL_URL':
      return { ...state, portalBaseUrl: action.portalBaseUrl };
    case 'SET_AUTH_PROVIDER':
      return { ...state, authProvider: action.authProvider, signinDomain: action.signinDomain };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'TOKEN_REFRESH':
      return { ...state, token: action.token };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    token: null,
    loading: true,
    devMode: false,
    portalBaseUrl: null,
    // 'firebase' (Portal handoff + Secure Token refresh) or 'google_identity'
    // (Cloud Identity-hosted accounts: backend-brokered Google sign-in + app
    // session tokens refreshed via /auth/refresh). Learned from /health.
    authProvider: 'firebase',
    signinDomain: null,
  });

  const tokenRef = useRef(state.token);
  tokenRef.current = state.token;

  // Mirror portalBaseUrl into a ref so the step-up handler (wired into api.js
  // via configureApi on mount) can read the latest value without re-wiring —
  // `signInViaPortal` is defined late in this component, so requireStepUp
  // builds the redirect URL itself from this ref (avoids the ordering issue).
  const portalBaseUrlRef = useRef(state.portalBaseUrl);
  portalBaseUrlRef.current = state.portalBaseUrl;
  const authProviderRef = useRef(state.authProvider);
  authProviderRef.current = state.authProvider;

  // Backend-brokered Google sign-in (google_identity). `hint` is the hosted
  // username (or the person's work email — the backend maps it).
  //
  // `reauth` (step-up / post-idle / post-ceiling) must make the person present
  // a factor again. Google's sign-in page ignores prompt=login / max_age and
  // the Directory sign-out takes up to an hour, so the backend routes
  // `reauth=1` through accounts.google.com/Logout?continue=<authorize URL>:
  // this browser's Google session is cleared and Google has to prompt for the
  // passkey / password + 2SV. (Every Google account in this browser profile
  // is signed out — a hosted ChargeStack login has no other use.)
  const startGoogleSignIn = useCallback((hint, opts = {}) => {
    const params = new URLSearchParams();
    if (hint) params.set('login_hint', hint);
    if (opts.reauth) params.set('reauth', '1');
    window.location.href = `/api/v1/auth/google/start?${params.toString()}`;
  }, []);

  // Counter of in-flight uploads. Bumped on `gcs_upload_start` postMessage
  // from the GcsUploader iframe, cleared on `gcs_upload_complete`. While > 0
  // the idle timer goes soft: we DON'T clear the token (which would 401 the
  // upload's `/complete` call and zombify it), but the user still has to
  // re-engage with the UI to keep their session interactively unlocked.
  const uploadsActiveRef = useRef(0);

  // Idle warning modal state: null, or { deadline } (ms epoch of the signout).
  // The shown-flag lives in a ref so the activity listener (inside the idle
  // effect) can consult it without re-running the effect.
  const [idleWarning, setIdleWarning] = useState(null);
  const idleWarningShownRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  // Absolute-ceiling heads-up: null, or { deadline } (ms epoch the 8h limit
  // ends this session). The ref remembers which sign-in it was shown for, so
  // the hourly token refresh doesn't raise it again after it's dismissed.
  const [sessionEnding, setSessionEnding] = useState(null);
  const sessionNoticeForRef = useRef(null);

  const logout = useCallback((reason, failedToken) => {
    idleWarningShownRef.current = false;
    setIdleWarning(null);
    setSessionEnding(null);
    const _current = localStorage.getItem('dc_token');
    // Cross-tab guard: localStorage is shared. If the credential that FAILED
    // (passed by api.js on a 401) isn't the one currently stored, a fresh
    // sign-in landed since — this stale tab must not revoke or clear the
    // newer session out from under it. (Without this, a crashed tab's dead
    // token killed every subsequent login within seconds.)
    const _newerSessionExists = Boolean(
      failedToken && _current && _current !== failedToken,
    );
    // Best-effort server-side session revocation (Tier 2 session hardening).
    // Raw fetch — NOT the api wrapper — so a 401 here can't re-enter the
    // logout/refresh path; keepalive lets it complete through the post-logout
    // redirect. DELIBERATE sign-out ONLY (the NavBar button). Not 'expired'
    // (credential already failed; in the stale-tab race the stored token
    // belongs to a NEWER session — revoking it is session murder). Not
    // 'idle' either: Firebase revocation is PER-USER, so one idle tab's
    // timeout was nuking the same user's live sessions in every other
    // browser — portal included ("Failed to launch Arc: user-token-expired",
    // 2026-07-20). An idle timeout ends THIS session locally (CJIS idle
    // termination); the 1h ID-token expiry + Tier 1 max-age bound the rest.
    if (_current && reason !== 'expired' && reason !== 'idle' && reason !== 'revoked' && !_newerSessionExists) {
      try {
        fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${_current}` },
          keepalive: true,
        }).catch(() => {});
      } catch { /* never block local logout */ }
    }
    if (!_newerSessionExists) {
      localStorage.removeItem('dc_token');
      localStorage.removeItem('dc_user');
      localStorage.removeItem('dc_refresh_token');
    }
    if (reason) sessionStorage.setItem('dc_logout_reason', reason);
    // Capture the page the user was on so we can land them back here after
    // they sign back in via the Portal (Portal redirects to /?token=... and
    // we then bounce to dc_return_to). Skip if we're already on /login or
    // the relink page.
    const here = window.location.pathname + window.location.search + window.location.hash;
    if (!here.startsWith('/login') && !here.startsWith('/auth/re-link-uploader')) {
      sessionStorage.setItem('dc_return_to', here);
    }
    dispatch({ type: 'LOGOUT' });
  }, []);

  // Raw Firebase ID-token refresh against the Secure Token API.
  // Returns the new ID token on success, null when there's nothing to refresh
  // against (no stored refresh_token / no API key). Throws on transient
  // failure so the periodic scheduler can pick its own retry cadence.
  const doTokenRefresh = useCallback(async () => {
    if (authProviderRef.current === 'google_identity') {
      // App session token: the backend re-issues it (same auth_time/sid)
      // while under the 8h ceiling and not revoked. A 401 here is
      // definitive — ceiling hit, revoked, or bad token — so latch dead.
      const current = localStorage.getItem('dc_token');
      if (!current || current === 'dev-mode') return null;
      const r = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { Authorization: `Bearer ${current}` },
      });
      if (!r.ok) {
        const err = new Error(`session-refresh-${r.status}`);
        if (r.status === 401) {
          err.dead = true;
          if (localStorage.getItem('dc_token') === current) markSessionDead();
        }
        throw err;
      }
      const data = await r.json();
      if (!data.token) throw new Error('session-refresh-malformed');
      localStorage.setItem('dc_token', data.token);
      dispatch({ type: 'TOKEN_REFRESH', token: data.token });
      return data.token;
    }
    const refreshToken = localStorage.getItem('dc_refresh_token');
    if (!refreshToken) {
      // Came in via a Portal flow that didn't forward a refresh_token; we
      // can't refresh. Caller falls back to logout when the ID token lapses.
      return null;
    }
    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
    if (!apiKey) return null;
    const r = await fetch(`https://securetoken.googleapis.com/v1/token?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
    });
    if (!r.ok) {
      // Distinguish definitive death from a blip. The Secure Token API answers
      // a revoked/expired/unknown refresh token with a 400 carrying a stable
      // error code; anything else (5xx, network) is transient and retryable.
      // A dead refresh token never comes back — latch api.js shut so no poller
      // can re-probe the backend with stale credentials, and tell callers via
      // err.dead so they log out instead of rescheduling.
      const errBody = await r.json().catch(() => null);
      const code = String(errBody?.error?.message || '');
      const DEAD_CODES = ['TOKEN_EXPIRED', 'USER_DISABLED', 'USER_NOT_FOUND', 'INVALID_REFRESH_TOKEN', 'MISSING_REFRESH_TOKEN'];
      const err = new Error(`token-refresh-${r.status}${code ? `-${code}` : ''}`);
      if (r.status === 400 && DEAD_CODES.some((c) => code.startsWith(c))) {
        err.dead = true;
        // Latch ONLY if the refresh token that died is still the stored one.
        // If a newer sign-in landed mid-flight (fresh dc_refresh_token), the
        // death verdict is about a stale credential — latching would freeze
        // the NEW session's tab on "Requests are paused".
        if (localStorage.getItem('dc_refresh_token') === refreshToken) {
          markSessionDead();
        }
      }
      throw err;
    }
    const data = await r.json();
    // Secure Token API uses snake_case; Firebase Admin uses camelCase.
    const idToken = data.id_token || data.idToken;
    const newRefresh = data.refresh_token || data.refreshToken;
    if (!idToken || !newRefresh) throw new Error('token-refresh-malformed');
    localStorage.setItem('dc_token', idToken);
    localStorage.setItem('dc_refresh_token', newRefresh);
    dispatch({ type: 'TOKEN_REFRESH', token: idToken });
    return idToken;
  }, []);

  // Deduped, never-throws wrapper for imperative 401 recovery (called by
  // api.js). When any authenticated request comes back 401 — the ID token
  // lapsed a hair before the proactive timer fired, a prior refresh blipped,
  // clock skew, etc. — api.js calls this and retries the request ONCE with the
  // returned token. Only a null return (refresh genuinely failed) logs the
  // user out. Concurrent 401s share one in-flight refresh so a burst of calls
  // doesn't stampede the Secure Token API. Returns the new token or null.
  const refreshInFlightRef = useRef(null);
  const refreshTokenNow = useCallback(() => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const p = doTokenRefresh()
      .catch(() => null)
      .finally(() => { refreshInFlightRef.current = null; });
    refreshInFlightRef.current = p;
    return p;
  }, [doTokenRefresh]);

  // Step-up re-auth (Tier 3 session hardening). Called by api.js when a critical
  // action (delete/export) returns a `step_up_required` 403 — the session is
  // valid but its auth is older than the step-up window. The SPA has no Firebase
  // Auth SDK (tokens come from the Portal), so re-auth reuses the Portal handoff.
  // Rather than yank the user away with no warning, `requireStepUp` raises a
  // heads-up prompt; only on confirm does `proceedStepUp` redirect to the Portal
  // with `reauth=1` (forcing a fresh password+MFA login → fresh `auth_time`).
  // dc_return_to survives the cross-origin round-trip via same-tab sessionStorage.
  const [stepUpPrompt, setStepUpPrompt] = useState(null);

  const requireStepUp = useCallback((detail) => {
    setStepUpPrompt(detail || {});
  }, []);

  const proceedStepUp = useCallback(() => {
    const here = window.location.pathname + window.location.search + window.location.hash;
    if (!here.startsWith('/login') && !here.startsWith('/auth/re-link-uploader')) {
      sessionStorage.setItem('dc_return_to', here);
    }
    sessionStorage.setItem('dc_logout_reason', 'step_up');
    if (authProviderRef.current === 'google_identity') {
      startGoogleSignIn(localStorage.getItem('dc_login_hint') || '', { reauth: true });
      return;
    }
    const portal = portalBaseUrlRef.current || 'https://www.chargestack.ai';
    window.location.href = `${portal}/?app=arc&reauth=1`;
  }, [startGoogleSignIn]);

  const cancelStepUp = useCallback(() => setStepUpPrompt(null), []);

  // Wire up api.js on mount
  useEffect(() => {
    configureApi(
      () => tokenRef.current,
      (failedToken, reason) => logout(reason || 'expired', failedToken),
      refreshTokenNow,
      requireStepUp,
    );
  }, [logout, refreshTokenNow, requireStepUp]);

  // Check health for dev_mode, then restore session or init Firebase
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const health = await fetchHealth();
        const devMode = health.dev_mode === true;

        if (cancelled) return;
        dispatch({ type: 'SET_DEV_MODE', devMode });
        const portalBaseUrl = health.config?.portal_base_url || null;
        if (portalBaseUrl) dispatch({ type: 'SET_PORTAL_URL', portalBaseUrl });
        const authProvider = health.config?.auth_provider || 'firebase';
        authProviderRef.current = authProvider;
        dispatch({ type: 'SET_AUTH_PROVIDER', authProvider, signinDomain: health.config?.signin_domain || null });

        if (devMode) {
          const devUser = { email: 'dev@localhost', uid: 'dev-user-local-123', name: 'Local Developer' };
          dispatch({ type: 'LOGIN', user: devUser, token: 'dev-mode', devMode: true });
          localStorage.setItem('dc_token', 'dev-mode');
          localStorage.setItem('dc_user', JSON.stringify(devUser));
          return;
        }

        // Portal redirect: extract the token. Pre-stashed into sessionStorage
        // by main.jsx BEFORE React/BrowserRouter mounted — that's necessary
        // because the catch-all `<Navigate to="/cases">` would otherwise drop
        // the URL fragment before this useEffect could read it.
        //
        // Falls back to reading window.location.{search,hash} directly if the
        // pre-stash didn't fire (e.g. an embedded mount that bypassed main.jsx,
        // or a future entry point that forgets the stash step).
        let urlToken = sessionStorage.getItem('dc_portal_token');
        let urlRefresh = sessionStorage.getItem('dc_portal_refresh_token');
        if (urlToken) {
          sessionStorage.removeItem('dc_portal_token');
          sessionStorage.removeItem('dc_portal_refresh_token');
        } else {
          const hashStr = window.location.hash.startsWith('#')
            ? window.location.hash.slice(1)
            : window.location.hash;
          const hashParams = new URLSearchParams(hashStr);
          const searchParams = new URLSearchParams(window.location.search);
          urlToken = hashParams.get('token') || searchParams.get('token');
          urlRefresh = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        }
        if (urlToken) {
          // Clean the token from the URL too (defence-in-depth — main.jsx
          // already did this if the pre-stash ran).
          const hashStr2 = window.location.hash.startsWith('#')
            ? window.location.hash.slice(1)
            : window.location.hash;
          const hashParams2 = new URLSearchParams(hashStr2);
          const searchParams2 = new URLSearchParams(window.location.search);
          hashParams2.delete('token');
          hashParams2.delete('refresh_token');
          searchParams2.delete('token');
          searchParams2.delete('refresh_token');
          const cleanSearch = searchParams2.toString() ? '?' + searchParams2.toString() : '';
          const cleanHash = hashParams2.toString() ? '#' + hashParams2.toString() : '';
          const cleanUrl = window.location.pathname + cleanSearch + cleanHash;
          window.history.replaceState({}, '', cleanUrl);

          // Decode JWT payload to extract user info
          try {
            const payloadB64 = urlToken.split('.')[1];
            const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
            const user = {
              email: payload.email || '',
              uid: payload.sub || payload.user_id || '',
              name: payload.name || (payload.email ? payload.email.split('@')[0] : 'User'),
            };
            localStorage.setItem('dc_token', urlToken);
            localStorage.setItem('dc_user', JSON.stringify(user));
            // google_identity: the token's email is the hosted username —
            // remember it so re-auth (expiry / step-up) can pre-select the
            // account on Google's page without asking again.
            if (payload.provider === 'google_identity' && payload.email) {
              localStorage.setItem('dc_login_hint', payload.email);
            }
            // Store the refresh token if Portal sent one; required for the
            // periodic ID-token refresh effect below. Without it, long
            // uploads still die at the 1h Firebase token expiry.
            if (urlRefresh) localStorage.setItem('dc_refresh_token', urlRefresh);
            // A Portal handoff is a genuine new sign-in — release the terminal
            // dead-session latch so the fresh credentials get a real attempt.
            resetAuthState();
            dispatch({ type: 'LOGIN', user, token: urlToken });
            // If we stashed a return location before the Portal round-trip,
            // bounce there now. Same-origin only.
            const returnTo = sessionStorage.getItem('dc_return_to');
            if (returnTo && returnTo.startsWith('/')) {
              sessionStorage.removeItem('dc_return_to');
              window.history.replaceState({}, '', returnTo);
            }
            return;
          } catch {
            // Malformed token — fall through to normal login
            console.warn('Failed to decode portal token');
          }
        }

        // Try restore from localStorage
        const savedToken = localStorage.getItem('dc_token');
        const savedUser = localStorage.getItem('dc_user');
        if (savedToken && savedToken !== 'dev-mode' && savedUser) {
          try {
            // Mirror the backend Tier 1 max-age check before resuming: a
            // token whose auth_time is past the ceiling would be rejected
            // server-side anyway (token refresh preserves auth_time), so
            // treat it as an expired session and require a fresh Portal
            // login instead of resuming into a wall of 401s. Missing
            // auth_time fails open — the backend still enforces.
            const payloadB64 = savedToken.split('.')[1];
            const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
            const authTimeMs = typeof payload.auth_time === 'number' ? payload.auth_time * 1000 : null;
            if (authTimeMs && Date.now() - authTimeMs > SESSION_MAX_AGE_MS) {
              localStorage.removeItem('dc_token');
              localStorage.removeItem('dc_user');
              localStorage.removeItem('dc_refresh_token');
              sessionStorage.setItem('dc_logout_reason', 'expired');
              dispatch({ type: 'SET_LOADING', loading: false });
              return;
            }
            const user = JSON.parse(savedUser);
            dispatch({ type: 'LOGIN', user, token: savedToken });
          } catch {
            localStorage.removeItem('dc_token');
            localStorage.removeItem('dc_user');
            dispatch({ type: 'SET_LOADING', loading: false });
          }
          return;
        }

        dispatch({ type: 'SET_LOADING', loading: false });
      } catch {
        // Health check failed — still allow login attempt
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  // Idle timeout (CJIS §5.4: re-auth after 30 min of user inactivity).
  // Soft-lock variant: while an upload is in flight, we don't tear down the
  // session — that would 401 every `/upload-progress/{sid}/complete` call
  // and zombify the upload. The user's interactive session is still locked
  // (the existing re-auth prompt fires once uploads finish or after a hard
  // ceiling), and the token continues to be refreshable until they re-auth.
  useEffect(() => {
    if (!state.user) return;

    lastActivityRef.current = Date.now();
    writeSharedActivity(lastActivityRef.current);
    let lastSharedWrite = lastActivityRef.current;
    // Once the warning is up, passive activity (a mouse nudge) must not
    // silently dismiss it — the user acknowledges via the modal button.
    const onActivity = () => {
      if (idleWarningShownRef.current) return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastSharedWrite > ACTIVITY_WRITE_THROTTLE_MS) {
        lastSharedWrite = now;
        writeSharedActivity(now);
      }
    };
    const listenerOpts = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach(ev => document.addEventListener(ev, onActivity, listenerOpts));
    // A recording that is PLAYING counts as activity (owner ruling
    // 2026-10-01): reviewing a bodycam video or a jail call is the work, and
    // it involves no input for longer than the idle window. `timeupdate` only
    // advances during playback (media events don't bubble — hence capture);
    // a paused or finished recording stops counting, and the absolute
    // session ceiling still bounds a recording left running unattended.
    const onMediaProgress = (e) => {
      const el = e.target;
      if (el instanceof HTMLMediaElement && !el.paused && !el.ended) onActivity();
    };
    document.addEventListener('timeupdate', onMediaProgress, listenerOpts);

    const timer = setInterval(() => {
      if (uploadsActiveRef.current > 0) return; // soft-lock: uploads in flight
      // The person is "active" if ANY tab of this session saw activity.
      const lastSeen = Math.max(lastActivityRef.current, readSharedActivity());
      const idleFor = Date.now() - lastSeen;
      if (idleFor > IDLE_TIMEOUT_MS) {
        logout('idle');
      } else if (idleFor > IDLE_TIMEOUT_MS - IDLE_WARNING_LEAD_MS && !idleWarningShownRef.current) {
        idleWarningShownRef.current = true;
        setIdleWarning({ deadline: lastSeen + IDLE_TIMEOUT_MS });
      } else if (idleWarningShownRef.current && idleFor < IDLE_TIMEOUT_MS - IDLE_WARNING_LEAD_MS) {
        // Another tab saw real activity after our warning went up — stand down.
        idleWarningShownRef.current = false;
        setIdleWarning(null);
      }
    }, IDLE_CHECK_INTERVAL);

    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach(ev => document.removeEventListener(ev, onActivity, listenerOpts));
      document.removeEventListener('timeupdate', onMediaProgress, listenerOpts);
    };
  }, [state.user, logout]);

  // Idle-clock exchange with the Portal dashboard tab (see IDLE_SYNC_MESSAGE).
  // Firebase/Portal sign-in only — the google_identity provider has no Portal.
  useEffect(() => {
    if (!state.user || state.authProvider !== 'firebase') return undefined;
    const trusted = new Set(PORTAL_PUBLIC_ORIGINS);
    try { if (state.portalBaseUrl) trusted.add(new URL(state.portalBaseUrl).origin); } catch { /* malformed */ }

    function onMessage(e) {
      if (!trusted.has(e.origin) || e.data?.type !== IDLE_SYNC_MESSAGE) return;
      const now = Date.now();
      const local = Math.max(lastActivityRef.current, readSharedActivity());
      // A time from the future would hold the session open — clamp to now.
      const remote = Math.min(Number(e.data.lastActivity) || 0, now);
      // The shared key, not lastActivityRef: the idle tick already reads it,
      // and its stand-down branch drops our warning if the Portal saw the
      // person after it went up.
      if (remote > local) writeSharedActivity(remote);
      try {
        e.source?.postMessage(
          { type: IDLE_SYNC_MESSAGE, lastActivity: Math.max(local, remote) },
          e.origin,
        );
      } catch { /* the dashboard tab is gone */ }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [state.user, state.authProvider, state.portalBaseUrl]);

  // Heads-up before the absolute session ceiling. Without it the 8h limit
  // lands as a 401 mid-click — the one sign-out that gave no warning at all.
  // `auth_time` survives token refresh, so the deadline is stable; a token
  // without it (or dev mode) simply gets no notice.
  useEffect(() => {
    if (!state.user || !state.token || state.devMode) return undefined;
    let authTimeMs = null;
    try {
      const payloadB64 = state.token.split('.')[1];
      const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof payload.auth_time === 'number') authTimeMs = payload.auth_time * 1000;
    } catch { /* opaque token */ }
    if (!authTimeMs || sessionNoticeForRef.current === authTimeMs) return undefined;
    const deadline = authTimeMs + SESSION_MAX_AGE_MS;
    const timer = setTimeout(() => {
      sessionNoticeForRef.current = authTimeMs;
      setSessionEnding({ deadline });
    }, Math.max(0, deadline - SESSION_END_NOTICE_LEAD_MS - Date.now()));
    return () => clearTimeout(timer);
  }, [state.user, state.token, state.devMode]);

  // "I'm still here" — reset the idle clock and drop the warning.
  const stayActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    writeSharedActivity(lastActivityRef.current);
    idleWarningShownRef.current = false;
    setIdleWarning(null);
  }, []);

  // Warning countdown lapsed — sign out now rather than waiting for the
  // next interval tick.
  const idleExpired = useCallback(() => {
    if (uploadsActiveRef.current > 0) { stayActive(); return; } // soft-lock
    logout('idle');
  }, [logout, stayActive]);

  // Track uploads in flight via postMessage from the GcsUploader iframe.
  // Also expose a force-refresh path the iframe can request when it observes
  // a 401 on `/upload-complete` or `/upload-progress/{sid}/complete`.
  useEffect(() => {
    function onMessage(e) {
      const t = e.data?.type;
      if (t === 'gcs_upload_start') uploadsActiveRef.current += 1;
      else if (t === 'gcs_upload_complete') uploadsActiveRef.current = 0;
      else if (t === 'gcs_token_refresh_request') forceTokenRefreshRef.current?.();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Periodic Firebase ID-token refresh via the Secure Token API.
  //
  // Why this is needed: Firebase ID tokens expire 1h after issuance. The SPA
  // had no refresh path, so every long-running session (uploads, multi-tab
  // case work) died at the 1h mark with a 401 → forced logout → in-flight
  // uploads zombified. Refresh schedules itself ~5 min before `exp` so the
  // window is always covered. Users without a stored refresh_token (legacy
  // Portal redirect that didn't forward it) skip the refresh and degrade to
  // the original 1h-then-die behavior, which is no worse than today.
  const forceTokenRefreshRef = useRef(null);
  useEffect(() => {
    if (!state.user || !state.token || state.devMode) return;

    let cancelled = false;
    let timer = null;

    function parseExpMs(jwt) {
      try {
        const payloadB64 = jwt.split('.')[1];
        const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
      } catch { return null; }
    }

    function schedule(delayMs) {
      if (cancelled) return;
      clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          const newToken = await doTokenRefresh();
          if (!newToken) {
            // No refresh capability; check back in case it gets added.
            schedule(TOKEN_REFRESH_FALLBACK_MS);
            return;
          }
          const expMs = parseExpMs(newToken);
          schedule(
            expMs ? Math.max(15_000, expMs - Date.now() - TOKEN_REFRESH_LEAD_MS)
                  : TOKEN_REFRESH_FALLBACK_MS,
          );
        } catch (e) {
          if (e && e.dead) {
            // The IdP definitively rejected our refresh token — this tab's
            // session is over. One clean signout; no reschedule, no further
            // network from this tab.
            logout('expired');
            return;
          }
          // Transient — retry; the iframe's 401-on-complete path will wait
          // for us. Don't logout here.
          schedule(TOKEN_REFRESH_RETRY_MS);
        }
      }, Math.max(0, delayMs));
    }

    // Expose imperative force-refresh for the iframe to call on 401.
    forceTokenRefreshRef.current = () => schedule(0);

    const expMs = parseExpMs(state.token);
    schedule(
      expMs ? Math.max(15_000, expMs - Date.now() - TOKEN_REFRESH_LEAD_MS)
            : TOKEN_REFRESH_FALLBACK_MS,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      forceTokenRefreshRef.current = null;
    };
  }, [state.user, state.token, state.devMode, doTokenRefresh, logout]);

  const login = useCallback(async () => {
    if (state.devMode) {
      const devUser = { email: 'dev@localhost', uid: 'dev-user-local-123', name: 'Local Developer' };
      dispatch({ type: 'LOGIN', user: devUser, token: 'dev-mode' });
      return;
    }

    // Firebase login
    const { initializeApp } = await import('firebase/app');
    const { getAuth, signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');

    const firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'cs-portal-dev-478221',
    };

    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    const token = await result.user.getIdToken();
    const user = {
      email: result.user.email,
      uid: result.user.uid,
      name: result.user.displayName,
    };

    localStorage.setItem('dc_token', token);
    localStorage.setItem('dc_refresh_token', result.user.refreshToken || '');
    localStorage.setItem('dc_user', JSON.stringify(user));
    resetAuthState();
    dispatch({ type: 'LOGIN', user, token });
  }, [state.devMode]);

  // Send the user to the Portal for sign-in. Portal supports email/password,
  // Google, SSO — whichever method the user is set up with. Portal will
  // redirect back here with ?token=... attached. Caller stashes return_to
  // via the logout flow above.
  const signInViaPortal = useCallback((opts = {}) => {
    if (state.authProvider === 'google_identity') {
      // Needs a hint to skip the email-first form; Login.jsx supplies it.
      startGoogleSignIn(opts.hint || localStorage.getItem('dc_login_hint') || '', { reauth: !!opts.reauth });
      return;
    }
    const portal = state.portalBaseUrl || 'https://www.chargestack.ai';
    // Portal already knows to redirect to the SPA after auth — no params needed.
    // But pass app=arc as a hint for which app the user came from, in case
    // the Portal uses it for messaging. `reauth=1` (step-up) forces the Portal
    // to drop the live session and require a fresh password+MFA login.
    const reauth = opts && opts.reauth ? '&reauth=1' : '';
    window.location.href = `${portal}/?app=arc${reauth}`;
  }, [state.portalBaseUrl, state.authProvider, startGoogleSignIn]);

  return (
    <AuthContext.Provider value={{ ...state, dispatch, login, logout, signInViaPortal, startGoogleSignIn }}>
      {children}
      {stepUpPrompt && (
        <StepUpModal
          action={stepUpPrompt}
          onContinue={proceedStepUp}
          onCancel={cancelStepUp}
          google={state.authProvider === 'google_identity'}
        />
      )}
      {idleWarning && (
        <IdleWarningModal deadline={idleWarning.deadline} onStay={stayActive} onExpire={idleExpired} />
      )}
      {sessionEnding && !idleWarning && (
        <SessionEndingNotice
          deadline={sessionEnding.deadline}
          onSignInNow={() => logout('expired')}
          onDismiss={() => setSessionEnding(null)}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
